import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Calendar, Star, FileText, LogOut, ChevronLeft, Bell, BellOff, Trash2, Loader2, BookOpen } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { isNativeApp } from "@/lib/platform";
import { SESSION_TYPES, modeKey } from "@/pages/SavedCharts";
import { enableDailyReading, disableDailyReading, isDailyReadingEnabled } from "@/lib/notifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, logout, deleteAccount, setUserData } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, personal: 0, compatibility: 0, lalkitab: 0 });
  const [sessions, setSessions] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [modeLoading, setModeLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/kundli/saved`);
        if (res.data.success) {
          const data = res.data.data;
          setSessions(data.slice(0, 5));
          const count = (m) => data.filter(d => modeKey(d) === m).length;
          setStats({ total: data.length, personal: count("personal"), compatibility: count("compatibility"), lalkitab: count("lalkitab") });
        }
      } catch (e) { /* ignore */ }
      finally { setLoadingStats(false); }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const handleDelete = async (e) => {
    e.preventDefault(); // keep the dialog open until the request finishes
    setDeleteError("");
    setDeleting(true);
    const res = await deleteAccount();
    setDeleting(false);
    if (res.success) navigate("/");
    else setDeleteError(res.error);
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="profile-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400 hover:text-amber-400 mb-6 gap-2" data-testid="back-to-dashboard">
        <ChevronLeft className="h-4 w-4" />Back to Dashboard
      </Button>

      {/* Profile header */}
      <div className="glass rounded-2xl p-6 glow-gold mb-6" data-testid="profile-card">
        <div className="flex items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-amber-500/30" data-testid="profile-avatar" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center border-2 border-amber-500/30">
              <User className="h-7 w-7 text-amber-400" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="font-cinzel text-xl sm:text-2xl font-bold text-amber-400 tracking-wide" data-testid="profile-name">
              {user.name}
            </h1>
            <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-1">
              <Mail className="h-3.5 w-3.5" />{user.email}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total Sessions", value: stats.total, icon: FileText, color: "text-amber-400" },
          { label: "Personal", value: stats.personal, icon: User, color: "text-violet-400" },
          { label: "Compatibility", value: stats.compatibility, icon: Star, color: "text-pink-400" },
          { label: "Lal Kitab", value: stats.lalkitab, icon: BookOpen, color: "text-orange-400" },
        ].map((s) => (
          <Card key={s.label} className="bg-black/30 border-white/10">
            <CardContent className="p-4 text-center">
              <s.icon className={`h-5 w-5 mx-auto mb-2 ${s.color}`} />
              <p className="text-2xl font-bold text-white font-data">{loadingStats ? "-" : s.value}</p>
              <p className="text-[10px] text-slate-500 mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent sessions */}
      <div className="glass rounded-2xl p-5 mb-6">
        <h2 className="font-cinzel text-sm font-bold text-amber-400 tracking-wide mb-4">Recent Sessions</h2>
        {sessions.length === 0 ? (
          <p className="text-xs text-slate-500 py-4 text-center">No sessions yet. Start a consultation to see them here.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">
                <div>
                  <p className="text-sm text-slate-200 font-medium">{s.name}</p>
                  <p className="text-[10px] text-slate-500 font-data">{s.kundli_data?.date_of_birth} | {s.kundli_data?.place_of_birth}</p>
                </div>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium border ${SESSION_TYPES[modeKey(s)].badge}`} data-testid={`profile-session-badge-${s.id}`}>
                  {SESSION_TYPES[modeKey(s)].short}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Separator className="bg-white/5 my-6" />

      {/* Daily Card-of-the-Day reminder (mobile only) */}
      {isNativeApp() && (
        <div className="glass rounded-2xl p-5 mb-6" data-testid="notif-toggle-card">
          <h2 className="font-cinzel text-sm font-bold text-amber-400 tracking-wide mb-3">Daily Reading Reminder</h2>
          <div className="flex items-center gap-3">
            <NotifToggle />
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            Get a gentle nudge at 9 AM with your Card of the Day. Tap to open the app.
          </p>
        </div>
      )}

      {/* Reading Mode */}
      <div className="glass rounded-2xl p-5 mb-6">
        <h2 className="font-cinzel text-sm font-bold text-amber-400 tracking-wide mb-4">Reading Mode</h2>
        <div className="flex gap-3">
          {[{id: "simple", label: "Simple", desc: "Short, clear answers"}, {id: "astrologer", label: "Astrologer", desc: "Detailed technical analysis"}].map(m => (
            <button key={m.id} disabled={modeLoading}
              onClick={async () => {
                setModeLoading(true);
                try {
                  const res = await axios.post(`${API}/user/settings`, { mode: m.id });
                  if (res.data.success) setUserData(res.data.user);
                } catch {}
                setModeLoading(false);
              }}
              className={`flex-1 p-3 rounded-xl border text-center transition-all ${user?.mode === m.id ? "border-amber-500/40 bg-amber-500/10" : "border-white/10 hover:border-white/20"}`}>
              <p className={`text-xs font-semibold ${user?.mode === m.id ? "text-amber-400" : "text-slate-300"}`}>{m.label}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{m.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Account info */}
      <div className="glass rounded-2xl p-5 mb-6">
        <h2 className="font-cinzel text-sm font-bold text-amber-400 tracking-wide mb-4">Account</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">User ID</span>
            <span className="text-xs text-slate-300 font-data">{user.user_id}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Joined</span>
            <span className="text-xs text-slate-300 font-data flex items-center gap-1.5">
              <Calendar className="h-3 w-3" />
              {user.created_at ? new Date(user.created_at).toLocaleDateString() : "Recently"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Auth Provider</span>
            <span className="text-xs text-slate-300">{user.auth_provider === "password" ? "Email & password" : "Google"}</span>
          </div>
        </div>
      </div>

      {/* Sign out */}
      <Button variant="outline" onClick={handleLogout} className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10 gap-2" data-testid="profile-logout">
        <LogOut className="h-4 w-4" />Sign Out
      </Button>

      {/* Delete account */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="w-full mt-4 text-xs text-slate-500 hover:text-red-400 flex items-center justify-center gap-1.5 py-2" data-testid="profile-delete-account">
            <Trash2 className="h-3.5 w-3.5" />Delete account
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-slate-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This permanently deletes your account, saved charts and readings. Purchased readings
              can't be restored. App Store or Google Play subscriptions must be cancelled in your
              store settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p className="text-xs text-rose-400" data-testid="delete-account-error">{deleteError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="border-white/10 bg-transparent text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-500 text-white gap-2" data-testid="confirm-delete-account">
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NotifToggle() {
  const [enabled, setEnabled] = useState(isDailyReadingEnabled());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const toggle = async () => {
    setBusy(true);
    setMsg("");
    if (enabled) {
      await disableDailyReading();
      setEnabled(false);
    } else {
      const r = await enableDailyReading(9, 0);
      if (r.scheduled) {
        setEnabled(true);
      } else if (r.reason === "denied") {
        setMsg("Permission denied. Enable notifications in iOS/Android settings.");
      } else {
        setMsg("Could not schedule reminder.");
      }
    }
    setBusy(false);
  };

  return (
    <>
      <button
        onClick={toggle}
        disabled={busy}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
          enabled ? "bg-amber-500 text-black" : "bg-white/5 border border-white/10 text-slate-300 hover:border-amber-500/40"
        }`}
        data-testid="daily-reading-toggle"
      >
        {enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
        {enabled ? "On — 9 AM daily" : "Turn on"}
      </button>
      {msg && <span className="text-[10px] text-amber-400">{msg}</span>}
    </>
  );
}
